/**
 * Version BÊTA des produits (03/10/2026) — ce que les fronts de la suite en
 * savent, et ce qu'ils en disent.
 *
 * Le portail décide (back-office › Bêta), les jetons le portent :
 *
 *  - `semply_beta`        : les produits (`app`, `compta`, `previ`, `immo`)
 *                           que ce compte voit en bêta ;
 *  - `semply_beta_compte` : compte de démonstration PARTAGÉ, verrouillé.
 *
 * Un produit en bêta affiche `<BandeauBeta />` en haut de chaque page et
 * `<AvertissementBeta />` à chaque connexion, filigrane ses PDF et refuse les
 * envois officiels (côté API). Un compte partagé voit grisé ce qui ferait
 * perdre la main sur le compte — et l'API le refuse (`beta_compte_verrouille`).
 *
 * Les TEXTES vivent ici, une fois pour toute la suite : bandeau, avertissement
 * et filigrane disent la même chose partout.
 */

export const TEXTES_BETA = {
  etiquette: 'Version bêta',
  bandeau:
    'Version de test partagée entre plusieurs utilisateurs : ne saisissez aucune donnée personnelle ni réelle. Documents sans valeur juridique, aucun envoi officiel.',
  titreAvertissement: 'Version bêta — environnement de test',
  avertissement: [
    'Vous utilisez une version de test, accessible à plusieurs utilisateurs : tout ce que vous enregistrez ici peut être vu par d’autres testeurs.',
    'N’y saisissez aucune donnée personnelle (noms, adresses, coordonnées bancaires, numéros d’identification…) ni aucune donnée réelle d’entreprise.',
    'Les documents produits portent la mention « Version test, aucune valeur juridique » et ne doivent pas être utilisés. Aucun envoi officiel n’est effectué (facturation électronique, déclarations).',
    'Les données de la version bêta peuvent être modifiées ou effacées à tout moment.',
  ],
  avertissementCompte:
    'Ce compte est partagé : mot de passe, membres, droits, invitations et abonnement ne peuvent pas y être modifiés.',
  confirmer: 'J’ai compris',
  filigrane: 'Version test, aucune valeur juridique',
  verrou: 'Désactivé : compte de démonstration partagé (version bêta)',
};

/** Code d'erreur des API quand un compte partagé tente une action verrouillée. */
export const CODE_COMPTE_BETA = 'beta_compte_verrouille';

/** Code d'erreur des API quand un envoi officiel est refusé en bêta. */
export const CODE_ENVOI_BETA = 'beta_envoi_bloque';

/** Charge utile d'un JWT, sans vérification (affichage seulement). `null` si illisible. */
export function claimsDuJeton(jeton) {
  if (typeof jeton !== 'string') return null;
  const partie = jeton.split('.')[1];
  if (!partie) return null;
  try {
    const base64 = partie.replace(/-/g, '+').replace(/_/g, '/');
    const brut = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    // UTF-8 : un nom accentué dans le jeton ne doit pas casser la lecture.
    const octets = Uint8Array.from(brut, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(octets));
  } catch {
    return null;
  }
}

/**
 * Ce que dit le jeton pour CE produit.
 * `claims` : la charge utile (id_token ou access token), ou le jeton lui-même.
 */
export function lireBeta(claims, produit) {
  const c = typeof claims === 'string' ? claimsDuJeton(claims) : claims;
  const liste = Array.isArray(c?.semply_beta) ? c.semply_beta : [];
  return {
    actif: liste.includes(String(produit).toLowerCase()),
    compte: c?.semply_beta_compte === true,
  };
}

// ── Avertissement : une fois par connexion ──────────────────────────────────
//
// « À chaque connexion » = à chaque nouvelle authentification au portail, que
// date `auth_time` : il ne bouge pas quand le jeton est renouvelé. Une seule
// clé par produit, réécrite à chaque connexion : rien ne s'accumule.

const cleStockage = (produit) => `semply-beta-vu:${String(produit).toLowerCase()}`;

/** Identifiant de la connexion courante : compte + date d'authentification. */
export function connexionDe(claims) {
  const c = typeof claims === 'string' ? claimsDuJeton(claims) : claims;
  if (!c?.sub) return null;
  return `${c.sub}:${c.auth_time ?? ''}`;
}

export function avertissementVu(produit, connexion) {
  if (!connexion) return false;
  try {
    return localStorage.getItem(cleStockage(produit)) === connexion;
  } catch {
    // Stockage indisponible (navigation privée stricte) : on le remontre.
    return false;
  }
}

export function marquerAvertissementVu(produit, connexion) {
  if (!connexion) return;
  try {
    localStorage.setItem(cleStockage(produit), connexion);
  } catch {
    // Sans stockage, l'avertissement reviendra au prochain chargement : sans gravité.
  }
}
